import { useContext, useState } from 'react';
import { GlobalContext } from '../../../context/GlobalContext';
import { unsafeAPI } from '../../../hooks/api';
import { Dialog } from '../../../components/Dialog';
import { Button } from '../../../shadcn/Button';
import Notice from '../../../components/Notice';
import { ShieldCheck, ShieldX } from 'lucide-react';
import { API_BASE } from '../../../utils/constants';

interface ApproveProviderDialogProps {
	providerIds: string[];
	action: 'approve' | 'reject';
	onClose: () => void;
	onActionComplete: () => void;
}

export default function ApproveProviderDialog({ providerIds, action, onClose, onActionComplete }: ApproveProviderDialogProps) {
	const { userData, internalCountry } = useContext(GlobalContext);
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState<string | null>(null);
	const [done, setDone] = useState(false);

	const isApprove = action === 'approve';
	const count = providerIds.length;

	const handleConfirm = async () => {
		setLoading(true);
		setError(null);

		try {
			const res = await unsafeAPI(
				'POST',
				`${API_BASE}/proveedores/approve/`,
				{ provider_ids: providerIds, action },
				userData,
				internalCountry?.get(),
			);

			if (res?.success) {
				setDone(true);
			} else {
				setError(res?.detail || res?.status || 'Error al procesar la solicitud');
			}
		} catch (err: any) {
			setError('Error: ' + err.message);
		} finally {
			setLoading(false);
		}
	};

	const handleClose = () => {
		if (done) onActionComplete();
		else onClose();
	};

	return (
		<Dialog
			title={isApprove ? 'Aprobar proveedores' : 'Rechazar proveedores'}
			onClose={handleClose}
			className="max-w-lg"
			noContentWrapper
			cerrarButton={false}
		>
			<div className="space-y-4 p-1 leading-normal text-neutral-200">
				<div className={`flex items-center gap-3 rounded-lg px-3 py-2 ${isApprove ? 'bg-amber-500/10' : 'bg-red-500/10'}`}>
					{isApprove ? (
						<ShieldCheck className="size-5 shrink-0 text-amber-400" />
					) : (
						<ShieldX className="size-5 shrink-0 text-red-400" />
					)}
					<p className={`text-sm ${isApprove ? 'text-amber-300' : 'text-red-300'}`}>
						{isApprove ? 'Se aprobarán' : 'Se rechazarán'} <strong>{count}</strong> proveedor{count !== 1 ? 'es' : ''}.
					</p>
				</div>

				{done ? (
					<>
						<Notice
							message={`${count} proveedor${count !== 1 ? 'es' : ''} ${isApprove ? 'aprobado' : 'rechazado'}${count !== 1 ? 's' : ''} correctamente.`}
							variant="success"
						/>
						<div className="flex justify-end border-t border-neutral-800 pt-4">
							<Button variant="primary" className="w-fit" onClick={handleClose}>
								Cerrar
							</Button>
						</div>
					</>
				) : (
					<>
						{error && <Notice message={error} variant="error" />}
						<div className="flex justify-end gap-3 border-t border-neutral-800 pt-4">
							<Button variant="secondary" className="w-fit" onClick={onClose} disabled={loading}>
								Cancelar
							</Button>
							<Button
								variant={isApprove ? 'primary' : 'destructive'}
								className="w-fit"
								onClick={handleConfirm}
								loading={loading}
							>
								{isApprove ? 'Aprobar' : 'Rechazar'}
							</Button>
						</div>
					</>
				)}
			</div>
		</Dialog>
	);
}
